import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    CardContent,
    IconButton,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router';
import { ArrowBack, NavigateNext } from '@mui/icons-material';
import { Experience } from '../../../types/experience';
import IndividualExperiencePhotos from '../IndividualExperiencePhotos';
import IndividualExperienceContent from '../IndividualExperienceContent';
import UserRole from '../../../../shared/AuthRoles';
import {
    useLazyGetOwnerExperiencesQuery,
    usePublishExperienceMutation,
    useUpdateExperienceMutation,
} from '../../../../app/Slices/SevenDayApiSlice';
import LoadingComponent from '../../../../layout/styledcomponents/LoadingAnimations/LoadingComponent';
import toast from '../../../shared/Toast';
import { camelCaseToTitleCase } from '../../../../shared/utils';
import { getStepSchema } from './schema';
import { useFormState } from './useFormState';
import SaveExperienceConfirmationModal from '../Owner/SharedComponents/SaveExperienceConfirmationModal';
import {
    getMissingRequiredPrices,
    needsPriceConfirmation,
} from './Pricing/utils';

const ExperiencePreview = ({ experience }: { experience: Experience }) => {
    return (
        <Card>
            <CardContent>
                <Stack spacing={2}>
                    <div>
                        <Typography variant='h2'>{experience.name}</Typography>
                        <Typography>
                            {experience.streetAddress}, {experience.suburb},{' '}
                            {experience.state}, {experience.postcode}
                        </Typography>
                    </div>
                    <IndividualExperiencePhotos experience={experience} />
                    <IndividualExperienceContent experience={experience} />
                </Stack>
            </CardContent>
        </Card>
    );
};

interface Error {
    reference: string;
    path: string;
    message: string;
}

const ReviewExperience = () => {
    const { experience, steps } = useFormState();

    const navigate = useNavigate();
    const loggedInUser = UserRole();

    const [publishExperience, { isLoading: isPublishing }] =
        usePublishExperienceMutation();
    const [updateExperience, { isLoading: isUpdating }] =
        useUpdateExperienceMutation();
    const [getOwnerExperiences] = useLazyGetOwnerExperiencesQuery();

    const [errors, setErrors] = useState<Error[]>([]);
    const [showPriceConfirmation, setShowPriceConfirmation] = useState(false);
    const [pendingAvailability, setPendingAvailability] = useState<
        boolean | null
    >(null);

    // ref to scroll errors into view
    const cardRef = useRef<HTMLDivElement>(null);

    const missingRequiredPrices = experience
        ? getMissingRequiredPrices(experience)
        : [];

    const completeListing = async (isAvailable: boolean) => {
        try {
            await updateExperience({
                owner: loggedInUser?.company,
                id: experience.id,
                experience: {
                    type: experience.type,
                    isAvailable,
                },
            }).unwrap();

            await publishExperience({
                owner: loggedInUser?.company,
                experience: experience.id,
            }).unwrap();

            toast.success(
                isAvailable
                    ? `Experience has been published`
                    : `Experience has been saved`,
            );
            navigate(
                `/viewExperiences/${loggedInUser?.company}/${experience.type}/${experience.id}`,
            );
        } catch (_error) {
            toast.error('Failed to save experience');
        }
    };

    const handleComplete = async (isAvailable: boolean) => {
        try {
            if (!experience) return;

            setErrors([]);

            const allAssets = await getOwnerExperiences(
                { owner: loggedInUser?.company },
                true,
            ).unwrap();

            const errors: Error[] = [];

            for (const step of steps) {
                const { schema } = getStepSchema(step, experience, allAssets);
                const result = schema.safeParse(experience);
                if (!result.success) {
                    result.error.issues.forEach((issue) => {
                        errors.push({
                            reference: step,
                            path: issue.path.join('.'),
                            message: issue.message,
                        });
                    });
                }
            }

            if (errors.length > 0) {
                setErrors(errors);
                return;
            }

            if (needsPriceConfirmation(experience)) {
                setPendingAvailability(isAvailable);
                setShowPriceConfirmation(true);
                return;
            }

            await completeListing(isAvailable);
        } catch (_error) {
            toast.error('Failed to save experience');
        }
    };

    const handleConfirmComplete = async () => {
        setShowPriceConfirmation(false);
        if (pendingAvailability === null) return;

        try {
            await completeListing(pendingAvailability);
        } catch (_error) {
            toast.error('Failed to save experience');
        } finally {
            setPendingAvailability(null);
        }
    };

    useEffect(() => {
        if (errors.length === 0) return;
        cardRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [errors]);

    if (!experience) return <LoadingComponent />;

    return (
        <>
            <Typography variant='h1'>Review Experience</Typography>
            <Stack spacing={2} sx={{ pt: 2 }}>
                <Typography>
                    Are you ready to publish your experience?
                </Typography>
                {errors.length > 0 && (
                    <Card ref={cardRef}>
                        <CardContent>
                            <Typography
                                sx={{ fontWeight: 'bold' }}
                                gutterBottom
                            >
                                The following issues must be addressed before
                                this experience can be saved:
                            </Typography>
                            <Stack spacing={1}>
                                {errors.map((error) => (
                                    <Alert
                                        key={`${error.reference}-${error.path}`}
                                        severity='error'
                                        action={
                                            <IconButton
                                                onClick={() =>
                                                    navigate(
                                                        `/experiences/new/${experience.id}/?step=${error.reference}`,
                                                    )
                                                }
                                            >
                                                <NavigateNext />
                                            </IconButton>
                                        }
                                    >
                                        <Typography sx={{ fontWeight: 'bold' }}>
                                            {camelCaseToTitleCase(
                                                error.reference,
                                            )}
                                        </Typography>
                                        <Typography>
                                            {camelCaseToTitleCase(error.path)}:{' '}
                                            {error.message}
                                        </Typography>
                                    </Alert>
                                ))}
                            </Stack>
                        </CardContent>
                    </Card>
                )}
                <Typography variant='h2'>Preview</Typography>
                <ExperiencePreview experience={experience} />
                <Paper
                    sx={(theme) => ({
                        display: 'flex',
                        position: 'sticky',
                        bottom: theme.spacing(2),
                        p: 2,
                        gap: 1,
                    })}
                >
                    <Button
                        startIcon={<ArrowBack />}
                        onClick={() => navigate(-1)}
                        disabled={isPublishing || isUpdating}
                    >
                        Back
                    </Button>
                    <Button
                        variant='outlined'
                        onClick={() => handleComplete(false)}
                        disabled={isPublishing || isUpdating}
                        sx={{ ml: 'auto' }}
                    >
                        Save without Publishing
                    </Button>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={() => handleComplete(true)}
                        disabled={isPublishing || isUpdating}
                    >
                        Publish
                    </Button>
                </Paper>
            </Stack>
            <SaveExperienceConfirmationModal
                experienceType={experience.type}
                open={showPriceConfirmation}
                onChange={handleConfirmComplete}
                onClose={() => {
                    setShowPriceConfirmation(false);
                    setPendingAvailability(null);
                }}
                priceRequired={missingRequiredPrices}
                action={pendingAvailability ? 'publish' : 'save'}
            />
        </>
    );
};

export default ReviewExperience;
